import React from 'react';
import DataEntrySummary, {DataEntrySummaryItem} from "../../components/DataEntry/DataEntrySummary";
import {LifestyleSpendingGoal} from "../models/LifestyleSpendingGoal";
import {formatCurrency, formatNumber, formatPercentWithWholeNumberAndTwoDecimals} from "../../utils/format";
import {ProfileResponse} from "../../ClientManagement/models/ProfileResponse";
import {PortfolioReserveResponse} from "../models/PortfolioReserve";
import {generateErrorableItem} from "../../components/DataEntry/DataEntryUtils";
import goalUtils from "../Summary/GoalUtils";
import {DiscountRateType} from "../models/DiscountRate";

type LifestyleSpendingReserveInformationProps = Readonly<{
    lifestyleSpendingGoal: LifestyleSpendingGoal,
    profile: ProfileResponse,
    portfolioReserve: PortfolioReserveResponse,
    hasError: boolean,
    rateType: DiscountRateType,
    enableEnhancedLifestyleFunding?: boolean
}>;

const formatReserveDiscountRate = (value?: number | null): string =>
    value == null ? "--" : `${formatNumber(value)}%`;

export default function LifestyleSpendingReserveInformation({
                                                                lifestyleSpendingGoal,
                                                                profile,
                                                                portfolioReserve,
                                                                hasError,
                                                                rateType,
                                                                enableEnhancedLifestyleFunding = false
                                                            }: LifestyleSpendingReserveInformationProps) {

    const reserveTargetText = profile.activePortfolioReserve ? 'Expected Reserve Length' : 'Reserve Target Length';
    const calculatedFields = lifestyleSpendingGoal.calculatedFields;
    const reserveInformation = lifestyleSpendingGoal.reserveInformation;
    const isTripleNet = rateType === DiscountRateType.TRIPLE_NET;

    const reserveTargetLength = enableEnhancedLifestyleFunding
        ? (reserveInformation?.portfolioReserveTargetLength ?? profile.portfolioReserveTargetLength)
        : profile.portfolioReserveTargetLength;
    const remainingPortfolioReserveYears = profile.activePortfolioReserve
        ? goalUtils.getRemainingPortfolioReserveLength(reserveTargetLength, profile.portfolioReserveActivationDate)
        : reserveTargetLength;

    const discountRateItemText = isTripleNet ? 'Triple Net Discount Rate' : 'Gross Discount Rate';

    const reserveTargetAmountValue = enableEnhancedLifestyleFunding
        ? formatCurrency(reserveInformation?.reserveTargetAmount)
        : formatCurrency(calculatedFields.reserveTargetAmount);
    const fundedByPortfolioReserveValue = enableEnhancedLifestyleFunding
        ? formatCurrency(reserveInformation?.fundedByPortfolioReserve)
        : formatCurrency(calculatedFields.portfolioReservePresentValue);
    const fundedByInflowsValue = enableEnhancedLifestyleFunding
        ? (reserveInformation?.fundedByInflows ?? 0) > 0
            ? `-${formatCurrency(reserveInformation?.fundedByInflows)}`
            : "$0"
        : formatCurrency(calculatedFields.portfolioReserveAlignedInflowPresentValue);
    const fundedBySavingsValue = (reserveInformation?.fundedBySavings ?? 0) > 0
        ? `-${formatCurrency(reserveInformation?.fundedBySavings)}`
        : "$0";
    const investableShortfallValue = (reserveInformation?.investableShortfall ?? 0) === 0
        ? "--"
        : `-${formatCurrency(reserveInformation?.investableShortfall)}`;

    const discountRateDisplayValue = enableEnhancedLifestyleFunding
        ? formatReserveDiscountRate(isTripleNet
            ? reserveInformation?.tripleNetDiscountRate
            : reserveInformation?.grossDiscountRate)
        : formatPercentWithWholeNumberAndTwoDecimals(isTripleNet
            ? portfolioReserve.tripleNetDiscountRate
            : portfolioReserve.grossDiscountRate);

    const reserveTargetAmountItem: DataEntrySummaryItem = generateErrorableItem('Reserve Target Amount', reserveTargetAmountValue, hasError);
    const fundedByPortfolioReserveItem: DataEntrySummaryItem = generateErrorableItem('Funded by Portfolio Reserve', fundedByPortfolioReserveValue, hasError);
    const fundedByInflowsItem: DataEntrySummaryItem = generateErrorableItem('Funded by Inflows', fundedByInflowsValue, hasError);
    const fundedBySavingsItem: DataEntrySummaryItem = generateErrorableItem('Funded by Savings', fundedBySavingsValue, hasError);
    const investableShortfallItem: DataEntrySummaryItem = generateErrorableItem('Investable Shortfall', investableShortfallValue, hasError);
    const reserveDiscountRateItem: DataEntrySummaryItem = generateErrorableItem(discountRateItemText, discountRateDisplayValue, hasError);

    return (
        <section>
            <DataEntrySummary title="Reserve Information"
                              items={[
                                  {
                                      label: reserveTargetText,
                                      value: (remainingPortfolioReserveYears + ' years'),
                                      testId: "reserve-target-length",
                                  },
                                  reserveTargetAmountItem,
                                  fundedByPortfolioReserveItem,
                                  fundedByInflowsItem,
                                  ...(enableEnhancedLifestyleFunding ? [fundedBySavingsItem, investableShortfallItem] : []),
                                  reserveDiscountRateItem
                              ]}
            />
        </section>
    )
}
